import React from "react";
import { Link } from "react-router-dom";
import {
  HomeIcon,
  UsersIcon,
  BriefcaseIcon,
  DocumentChartBarIcon,
  PuzzlePieceIcon,
  BugAntIcon,
  CubeIcon,
  ShieldCheckIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  ChevronDownIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { type MenuEntry, type PageKey, pageKeyToPath } from "../lib/menu";

type Props = {
  expanded?: boolean;
  toggleExpanded?: () => void;
  menu: MenuEntry[];
  reportsOpen: boolean;
  setReportsOpen: (open: boolean) => void;
  activePage: PageKey;
  reportsView: "fakturor" | "klimat";
  setReportsView: (view: "fakturor" | "klimat") => void;
  setActivePage: (page: PageKey, opts?: { skipNavigate?: boolean }) => void;
  setMenuOpen: (open: boolean) => void;
  isCustomerPortal: boolean;
  startHomeSentinel?: () => void;
  stopHomeSentinel?: () => void;
  fetchFirstPage?: () => void | Promise<void>;
  onLogout?: () => void;
  showLogout?: boolean;
};

export function SideMenu({
  expanded = false,
  toggleExpanded,
  menu,
  reportsOpen,
  setReportsOpen,
  activePage,
  reportsView,
  setReportsView,
  setActivePage,
  setMenuOpen,
  isCustomerPortal,
  startHomeSentinel,
  stopHomeSentinel,
  fetchFirstPage,
  onLogout,
  showLogout,
}: Props): JSX.Element {
  const ICONS: Record<PageKey, JSX.Element> = {
    home: <HomeIcon className="h-7 w-7" />,
    users: <UsersIcon className="h-7 w-7" />,
    fakturering: <BriefcaseIcon className="h-7 w-7" />,
    rapporter: <DocumentChartBarIcon className="h-7 w-7" />,
    productTypesAdmin: <PuzzlePieceIcon className="h-7 w-7" />,
    bugBoard: <BugAntIcon className="h-7 w-7" />,
    vidareforsaljning: <CubeIcon className="h-7 w-7" />,
    kunder: <UserGroupIcon className="h-7 w-7" />,
    certificates: <ShieldCheckIcon className="h-7 w-7" />,
  };

  const handleMenuSelect = (page: PageKey) => {
    setActivePage(page, { skipNavigate: true });

    if (page === "home") {
      fetchFirstPage?.();
      startHomeSentinel?.();
    } else {
      stopHomeSentinel?.();
    }
  };

  return (
    <>
      {expanded && (
        <div
          className="fixed inset-0 z-[1000] bg-black/20 backdrop-blur-sm transition-opacity duration-200"
          onClick={toggleExpanded}
        />
      )}
      <aside
        id="gw-side-menu"
        data-expanded={expanded}
        className="gw-side-menu fixed inset-y-0 left-0 z-[1001] flex bg-surface max-w-[85vw] flex-col gap-3 text-text border-r border-border shadow-[2px_0_12px_rgba(0,0,0,0.15)] p-3 transition-all duration-200 overflow-hidden overflow-x-hidden"
        role="navigation"
        aria-label="Meny"
      >
        <button
          type="button"
          onClick={toggleExpanded}
          className="menu-item gw-btn gw-btn-ghost justify-start w-full focus:outline-none focus:ring-0 group"
          aria-label={expanded ? "Stäng meny" : "Öppna meny"}
        >
          <span className="flex items-center gap-2 truncate">
            <span className="text-lg text-muted transition transform group-hover:text-primary group-hover:scale-110 group-active:scale-95">
              <Bars3Icon className="h-7 w-7" />
            </span>
            <span
              className={`text-lg font-semibold transition-opacity duration-150 ${
                expanded ? "opacity-100" : "opacity-0"
              }`}
            >
              Meny
            </span>
          </span>
        </button>

        <nav className="menu-list grid w-full gap-2 overflow-y-auto overflow-x-hidden pr-1">
          {menu
            .filter((m) => m.visible?.() ?? true)
            .map((m) => {
              if (isCustomerPortal && (m.key === "fakturering" || m.key === "vidareforsaljning" || m.key === "certificates")) return null;

              if (m.key === "rapporter") {
                const isRapporterActive = activePage === "rapporter";
                return (
                  <div key="rapporter" className="grid gap-1.5">
                    <button
                      type="button"
                      className="menu-item gw-btn gw-btn-ghost justify-start w-full focus:outline-none focus:ring-0 group"
                      aria-expanded={reportsOpen}
                      onClick={() => setReportsOpen(!reportsOpen)}
                    >
                      <span className="flex items-center gap-3 truncate">
                        {/* Container for Icon + Chevron */}
                        <div className="flex flex-col items-center">
                          <span className="text-lg text-muted transition transform group-hover:text-primary group-hover:scale-110 group-active:scale-95">
                            {ICONS[m.key] ?? <DocumentChartBarIcon className="h-7 w-7" />}
                          </span>

                          <ChevronDownIcon
                            className={`w-4 h-4 mt-0.5 text-muted transition-transform duration-200 ease-in-out ${
                              reportsOpen ? "rotate-0" : "-rotate-90"
                            }`}
                          />
                        </div>

                        {/* Label */}
                        <span className={`transition-opacity duration-150 ${expanded ? "opacity-100" : "opacity-0"}`}>
                          {m.label}
                        </span>
                      </span>
                    </button>

                    {reportsOpen && (
                      <div className="ml-2 grid gap-1.5">
                        {!isCustomerPortal && (
                          <Link
                            to="/rapporter"
                            className="menu-item gw-btn gw-btn-ghost justify-start w-full focus:outline-none focus:ring-0 group"
                            onClick={() => {
                              setReportsView("fakturor");
                              handleMenuSelect("rapporter");
                            }}
                          >
                            <span className="flex items-center gap-2 truncate">
                              <span className="text-lg text-muted transition transform group-hover:text-primary group-hover:scale-110 group-active:scale-95">
                                <DocumentChartBarIcon className="h-6 w-6" />
                              </span>
                              <span
                                className={`transition-opacity duration-150 ${
                                  expanded ? "opacity-100" : "opacity-0"
                                }`}
                              >
                                Fakturarapporter
                              </span>
                            </span>
                          </Link>
                        )}

                        <Link
                          to="/rapporter/klimat"
                          className="menu-item gw-btn gw-btn-ghost justify-start w-full focus:outline-none focus:ring-0 group"
                          onClick={() => {
                            setReportsView("klimat");
                            handleMenuSelect("rapporter");
                          }}
                        >
                          <span className="flex items-center gap-2 truncate">
                            <span className="text-lg text-muted transition transform group-hover:text-primary group-hover:scale-110 group-active:scale-95">
                              <DocumentChartBarIcon className="h-6 w-6" />
                            </span>
                            <span
                              className={`transition-opacity duration-150 ${
                                expanded ? "opacity-100" : "opacity-0"
                              }`}
                            >
                              Klimatrapport
                            </span>
                          </span>
                        </Link>
                      </div>
                    )}
                  </div>
                );
              }

              const active = activePage === m.key;
              const to = pageKeyToPath(m.key, { reportsView });
              return (
                <Link
                  key={m.key}
                  to={to}
                  className="menu-item gw-btn gw-btn-ghost justify-start w-full focus:outline-none focus:ring-0 group"
                  onClick={() => handleMenuSelect(m.key)}
                >
                  <span className="flex items-center gap-2 truncate">
                    <span className="text-lg text-muted transition transform group-hover:text-primary group-hover:scale-110 group-active:scale-95">
                      {ICONS[m.key] ?? <CubeIcon className="h-7 w-7" />}
                    </span>
                    <span className={`transition-opacity duration-150 ${expanded ? "opacity-100" : "opacity-0"}`}>
                      {m.label}
                    </span>
                  </span>
                </Link>
              );
            })}
        </nav>

        {showLogout && onLogout && (
          <div className="mt-auto">
            <button
              className="gw-btn gw-btn-ghost w-full justify-start focus:outline-none focus:ring-0 group"
              onClick={onLogout}
            >
              <span className="flex items-center gap-2">
                <span className="text-lg text-muted transition transform group-hover:text-primary group-hover:scale-110 group-active:scale-95">
                  <ArrowRightOnRectangleIcon className="h-7 w-7" />
                </span>
                <span className={`transition-opacity duration-150 ${expanded ? "opacity-100" : "opacity-0"}`}>
                  Logga ut
                </span>
              </span>
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
